import React from "react";
import { Dialog } from "../../../ui/components/Dialog";
import { Button } from "../../../ui/components/Button";
import { FeatherInfo } from "@subframe/core";

export interface ApArWorkbenchUpdateConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when user clicks "Proceed". Parent continues with the action (FBAC or direct). */
  onConfirm: () => void;
  /** Optional: "customer" or "vendor" to tailor the message. Defaults to "customer/vendor". */
  entity?: "customer" | "vendor";
  /** Whether the confirm action is in progress. */
  loading?: boolean;
}

const MESSAGE_CUSTOMER =
  "Since you are unmatching a payment made to a customer, the AP/AR reports for the workbench will be updated for this customer in the next 15 minutes.";
const MESSAGE_VENDOR =
  "Since you are unmatching a payment made to a vendor, the AP/AR reports for the workbench will be updated for this vendor in the next 15 minutes.";
const MESSAGE_DEFAULT =
  "Since you are deleting a payment made, the AP/AR reports for the workbench will be updated for this customer/vendor in the next 15 minutes.";

/**
 * Modal shown before unmatch (customer/vendor payment) or AP/AR journal delete.
 * Informs that workbench AP/AR reports will update in 15 minutes; on confirm, parent proceeds with the action.
 */
export const ApArWorkbenchUpdateConfirmModal: React.FC<
  ApArWorkbenchUpdateConfirmModalProps
> = ({ open, onOpenChange, onConfirm, entity, loading = false }) => {
  const displayMessage =
    entity === "customer"
      ? MESSAGE_CUSTOMER
      : entity === "vendor"
        ? MESSAGE_VENDOR
        : MESSAGE_DEFAULT;

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[420px] max-w-[520px]">
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100">
              <FeatherInfo className="h-5 w-5 text-brand-600" />
            </div>
            <span className="text-heading-2 font-heading-2 text-default-font">
              AP/AR workbench update
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-4 py-4">
            <span className="text-body font-body text-neutral-700 leading-relaxed">
              {displayMessage}
            </span>
          </div>

          <div className="flex w-full items-center justify-end gap-3 pt-1">
            <Button
              variant="neutral-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleConfirm}
              loading={loading}
              disabled={loading}
            >
              Proceed
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
